export { ActionIcons, ExternalLinkIcon, type IconProps } from './action-icons';
export { ActionModal, type ActionModalProps } from './action-modal';
export { AddRowButton, type AddRowButtonProps } from './add-row-button';
export {
  AiChatMessages,
  defaultIsPartVisible,
  getAssistantMessageText,
  type AiChatMessagesProps,
  type RenderPartContext,
} from './ai-chat-messages';
export type {
  AddMessageOptions,
  AiChatMessage,
  AiChatMessagePart,
  LegacyChatMessage,
  Suggestions,
  Widget,
  WidgetsMap,
} from './ai-chat-types';
export { FeedbackOptions, type FeedbackOptionsProps } from './feedback-options';
export {
  AiChatDownloadableFile,
  type AiChatDownloadableFileProps,
} from './ai-chat-downloadable-file';
export { AiChatWindowImage, isImagePart } from './ai-chat-window-image';
export { default as AiChatWindowMessage } from './ai-chat-window-message';
export {
  AI_TEXTAREA_DEFAULT_ACCEPTED_FILE_TYPES,
  AiTextarea,
  FileDropOverlay,
  PERSONA_CHAT_MAX_IMAGE_FILES,
  useFileDrop,
  type AiTextareaHandle,
  type AiTextareaSubmitPayload,
  type FileDropOverlayProps,
} from './ai-textarea';
export { usePlaceholder } from './ai-textarea/hooks';
export { SuggestionsWidget } from './ai-widgets';
export {
  AnimatedHeight,
  type AnimatedHeightProps,
  type HeightChangePhase,
} from './animated-height';
export {
  AssetGallery,
  AssetGalleryModal,
  type Asset,
  type AssetGalleryModalProps,
  type AssetGalleryProps,
} from './asset-gallery';
export { AssetThumbnail, type AssetThumbnailProps } from './asset-thumbnail';
export { AssetUploader, type AssetUploaderProps } from './asset-uploader';
export { AutoSaveStatus } from './auto-save-status';
export {
  BatchOperationError,
  DefaultSupportLink,
  type BatchOperationItem,
} from './batch-operation-error';
export { BetaBadge, type BetaBadgeProps } from './beta-badge';
export { CardHeader, type CardHeaderProps } from './card-header';
export {
  CascadingSelect,
  type CascadingSelectEntry,
  type CascadingSelectItem,
  type CascadingSelectProps,
} from './cascading-select';
export { CopyableValue } from './copyable-value';
export { CountPill, type CountPillProps } from './count-pill';
export { CenteredAbsoluteLoader } from './centered-absolute-loader';
export { CollapsibleCard, type CollapsibleCardProps } from './collapsible-card';
export { useConfirmDeleteBanner } from './confirm-banner';
export { CreatableMultiSelect, type CreatableMultiSelectProps } from './creatable-multi-select';
export { CreatableSelect, type CreatableSelectProps } from './creatable-select';
export { InlineRenameField, type InlineRenameFieldProps } from './inline-rename-field';
export { InlineSelect, type InlineSelectProps } from './inline-select';
export { DragToAddCard } from './drag-to-add-card';
export { default as ControlBar } from './control-bar';
export { DownloadCard } from './download-card';
export { DraftReadyMenu } from './draft-ready-menu';
export { ExternalLink, type ExternalLinkProps } from './external-link';
export { FACEPILE_DEFAULT_MAX_VISIBLE, Facepile, type FacepileProps } from './facepile';
export { FeatureHeader } from './feature-header';
export { FieldRow, type FieldRowProps } from './field-row';
export {
  GuidedStepCardSwap,
  GuidedStepCardViewport,
  GuidedStepFooter,
  GuidedStepMain,
  GuidedStepRadioButton,
  GuidedStepRadioButtonGroup,
} from './guided-step';
export type {
  GuidedStepCardSwapProps,
  GuidedStepCardViewportProps,
  GuidedStepFooterLayout,
  GuidedStepFooterProps,
  GuidedStepMainLayout,
  GuidedStepMainProps,
  GuidedStepRadioButtonGroupProps,
  GuidedStepRadioButtonProps,
  GuidedStepRadioButtonLargeProps,
} from './guided-step';
export { HighlightLogo, LOGO_SRC, LOGO_VARIANT } from './highlight-logo';
export { InfoCta } from './info-cta';
export { ImageWithLoader } from './image-with-loader';
export {
  CreditCostInput,
  EmailInput,
  HubspotDealIdInput,
  HubspotProjectIdInput,
  InlineEditableInput,
  NumberStepper,
} from './inputs';
export type { AtMentionOption, InlineEditableInputProps, NumberStepperProps } from './inputs';
export { MarkdownProse, type MarkdownProseProps } from './markdown-prose';
export { ModalButtons, type ModalButtonsProps } from './modal-buttons';
export { MonospaceTextArea, type MonospaceTextAreaProps } from './monospace-text-area';
export { NewBadge, type NewBadgeProps } from './new-badge';
export { PoweredByHighlightAI } from './powered-by-highlight-ai';
export { ProductCard, type ProductCardProps } from './product-card';
export { PulseLoader, type PulseLoaderProps } from './pulse-loader';
export { PULSE_MARK_DOTS, PulseLogo, type PulseLogoProps } from './pulse-logo';
export { default as PromptButton } from './prompt-button/prompt-button';
export type { PromptButtonProps } from './prompt-button/prompt-button';
export {
  PromptPill,
  PromptPills,
  type PromptPillProps,
  type PromptPillsProps,
} from './prompt-pills';
export { default as RightSidebarCard } from './right-sidebar-card';
export type { RightSidebarCardTab } from './right-sidebar-card';
export { SearchInput, type SearchInputProps } from './search-input';
export { SetupStepCard, type SetupStepCardProps } from './setup-step-card';
export { SidebarTabs } from './sidebar-tabs';
export {
  PROGRESS_STATUS_LABEL,
  StatusColorDot,
  type ProgressStatus,
  type StatusColorDotProps,
} from './status-color-dot';
export {
  StatusBadge,
  getStatusBadgeColor,
  getStatusBadgeLabel,
  type StatusBadgeProps,
} from './status-badge';
export { StatusBanner } from './status-banner';
export { SvgIcons } from './svg-icons';
export { Author, DateCell, DateTimeCell } from './table-cells';
export { TabsNav, type TabData } from './tabs-nav';
export {
  TIMELINE_BAR_HEIGHT,
  TIMELINE_DAY_WIDTH,
  TIMELINE_PALETTE_CLASS_NAME,
  TimelineBar,
  type TimelineBarProps,
} from './timeline-bar';
export { byStart, type LanedEntry, type TimelinePlaced, toLanes } from './timeline-lanes';
export {
  ToggleableList,
  type ToggleableListItem,
  type ToggleableListProps,
} from './toggleable-list';
export { TopNav, TEST_ID as TOP_NAV_TEST_ID } from './top-nav';
export type { TopNavProps, TopNavViewer } from './top-nav';
export { useUuidPasteBin } from './uuid-pastebin';
export { ZeroStateMarketing } from './zero-state-marketing/zero-state-marketing';
